<template>
  <v-card>
    <!-- Header Section -->
    <v-card-title class="d-flex justify-space-between align-center">
      <div>
        <div class="headline">Closed Cases in Period</div>
        <div class="d-flex flex-wrap mt-1" style="gap: 16px;">
          <v-chip small outlined>
            <v-icon small left>mdi-file-document-outline</v-icon>
            {{ totalFilteredResults }} Cases
          </v-chip>
          <v-chip small outlined color="warning">
            <v-icon small left>mdi-alert-outline</v-icon>
            {{ notSettledAfterDemand }} Not Settled After Demand ({{
              notSettledAfterDemandPercentage
            }}%)
          </v-chip>
          <v-chip small outlined color="info">
            <v-icon small left>mdi-clock-outline</v-icon>
            Avg TOD: {{ averageTod }}
          </v-chip>
          <v-chip small outlined color="error">
            <v-icon small left>mdi-currency-usd-off</v-icon>
            {{ closedWithoutFees }} No Fees ({{ closedWithoutFeesPercentage }}%)
          </v-chip>
          <v-chip small outlined color="deep-orange">
            <v-icon small left>mdi-account-remove-outline</v-icon>
            {{ firedByClient }} Fired ({{ firedByClientPercentage }}%)
          </v-chip>
        </div>
      </div>
      <v-btn text small @click="showCharts = !showCharts">
        <v-icon left>{{
          showCharts ? "mdi-chevron-up" : "mdi-chart-bar"
        }}</v-icon>
        {{ showCharts ? "Hide Charts" : "Show Charts" }}
      </v-btn>
    </v-card-title>

    <!-- Collapsible Charts Section -->
    <v-expand-transition>
      <div v-show="showCharts">
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <v-row>
            <v-col cols="12" md="7">
              <div class="d-flex align-center mb-2">
                <span class="text-subtitle-2 mr-4">Chart View:</span>
                <v-btn-toggle v-model="showByAttorney" mandatory dense>
                  <v-btn :value="false" small>By Reason</v-btn>
                  <v-btn :value="true" small>By Attorney</v-btn>
                </v-btn-toggle>
              </div>
              <org-closed-cases-in-period
                :options="
                  showByAttorney
                    ? closedByAttorneyOptions
                    : orgClosedCasesOptions
                "
              ></org-closed-cases-in-period>
            </v-col>
            <v-col cols="12" md="5">
              <div class="text-subtitle-2 mb-2">TOD by Attorney</div>
              <org-to-d-by-attorney :attorney-data="averageTodByAttorney" />
            </v-col>
          </v-row>
          <v-divider class="my-4"></v-divider>
          <div class="text-subtitle-2 mb-2">Stats by Case Group</div>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Case Group</th>
                  <th>Litigation Status</th>
                  <th class="text-center">Total</th>
                  <th class="text-center">Not Settled After Demand</th>
                  <th class="text-center">Avg TOD (Fees)</th>
                  <th class="text-center">Avg TOD (No Fees)</th>
                  <th class="text-center">Has Fees</th>
                  <th class="text-center">No Fees</th>
                  <th class="text-center">Fired</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in statsByCaseGroup" :key="row.key">
                  <td>{{ row.caseGroup }}</td>
                  <td>{{ row.litigationStatus }}</td>
                  <td class="text-center">{{ row.total }}</td>
                  <td class="text-center">
                    {{ row.notSettledAfterDemand }}
                    <span class="grey--text text--darken-1"
                      >({{ row.notSettledAfterDemandPct }}%)</span
                    >
                  </td>
                  <td class="text-center">{{ row.avgTod }}</td>
                  <td class="text-center">{{ row.avgTodNoFees }}</td>
                  <td class="text-center">{{ row.hasFees }}</td>
                  <td class="text-center">
                    {{ row.noFees }}
                    <span class="grey--text text--darken-1"
                      >({{ row.noFeesPct }}%)</span
                    >
                  </td>
                  <td class="text-center">
                    {{ row.fired }}
                    <span class="grey--text text--darken-1"
                      >({{ row.firedPct }}%)</span
                    >
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="font-weight-bold">
                  <td colspan="2">Totals</td>
                  <td class="text-center">
                    {{ statsByCaseGroupTotals.total }}
                  </td>
                  <td class="text-center">
                    {{ statsByCaseGroupTotals.notSettledAfterDemand }}
                    <span class="grey--text text--darken-1"
                      >({{
                        statsByCaseGroupTotals.notSettledAfterDemandPct
                      }}%)</span
                    >
                  </td>
                  <td class="text-center">
                    {{ statsByCaseGroupTotals.avgTod }}
                  </td>
                  <td class="text-center">
                    {{ statsByCaseGroupTotals.avgTodNoFees }}
                  </td>
                  <td class="text-center">
                    {{ statsByCaseGroupTotals.hasFees }}
                  </td>
                  <td class="text-center">
                    {{ statsByCaseGroupTotals.noFees }}
                    <span class="grey--text text--darken-1"
                      >({{ statsByCaseGroupTotals.noFeesPct }}%)</span
                    >
                  </td>
                  <td class="text-center">
                    {{ statsByCaseGroupTotals.fired }}
                    <span class="grey--text text--darken-1"
                      >({{ statsByCaseGroupTotals.firedPct }}%)</span
                    >
                  </td>
                </tr>
              </tfoot>
            </template>
          </v-simple-table>
        </v-card-text>
      </div>
    </v-expand-transition>

    <v-divider></v-divider>

    <!-- Table Controls -->
    <v-card-text class="py-2">
      <v-row align="center" dense>
        <!-- Filters -->
        <v-col cols="auto">
          <v-switch
            v-model="retainedOnly"
            label="Retained Only"
            hide-details
            dense
            class="mt-0"
          ></v-switch>
        </v-col>
        <v-col cols="auto">
          <v-switch
            v-model="needsSettlementOnly"
            label="Needs Settlement"
            hide-details
            dense
            class="mt-0"
          ></v-switch>
        </v-col>
        <v-col cols="auto" style="min-width: 150px;">
          <v-select
            v-model="reviewFilter"
            :items="reviewFilterOptions"
            label="Review Filter"
            hide-details
            dense
            outlined
          ></v-select>
        </v-col>

        <v-spacer></v-spacer>

        <!-- Search and Actions -->
        <v-col cols="auto" style="min-width: 200px;">
          <v-text-field
            @keyup="updateSearch"
            append-icon="mdi-magnify"
            label="Search"
            hide-details
            dense
            outlined
            single-line
          ></v-text-field>
        </v-col>
        <v-col cols="auto">
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn small outlined v-bind="attrs" v-on="on">
                <v-icon small left>mdi-view-column</v-icon>
                Columns
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                v-for="header in optionalHeaders"
                :key="header.value"
                @click="toggleColumnVisibility(header.value)"
              >
                <v-list-item-action class="mr-2">
                  <v-checkbox
                    :input-value="isColumnVisible(header.value)"
                    color="primary"
                    dense
                    hide-details
                  ></v-checkbox>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>{{ header.text }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-col>
        <v-col cols="auto">
          <vue-json-to-csv :json-data="casesFiltered" csv-title="Closed_Report">
            <v-btn small outlined>
              <v-icon small left>mdi-download</v-icon>
              Export
            </v-btn>
          </vue-json-to-csv>
        </v-col>
      </v-row>
    </v-card-text>

    <!-- Data Table -->
    <v-data-table
      :footer-props="{
        'items-per-page-options': [-1]
      }"
      :headers="visibleHeaders"
      hide-default-footer
      :items="casesFiltered"
      :item-key="Math.random(10).toString()"
      :loading="$apollo.loading"
      :search="search"
      :sort-by="['dateClosed']"
      :sort-desc="[true]"
      :item-class="getRowClass"
      @current-items="getFiltered"
    >
      <template v-slot:item.caseId="props">
        <a
          class="go-to-case-link mr-2"
          target="_blank"
          :href="
            `https://cs-web.airdesksolutions.com/SA/CaseSummary.aspx?CaseID=${props.item.caseId}`
          "
        >
          <v-icon size="15">mdi-open-in-new</v-icon>
        </a>
      </template>
      <template v-slot:item.intakeRank="props">
        <v-rating
          :value="intakeRank(props.item.intakeRank)"
          background-color="white"
          color="yellow accent-4"
          dense
          hover
          readonly
          size="12"
          v-if="intakeRank(props.item.intakeRank) > 0"
        ></v-rating>
      </template>
      <template v-slot:item.totalFees="props">
        <span v-if="props.item.totalFees">
          ${{ Number(props.item.totalFees).toLocaleString() }}
        </span>
        <span v-else>-</span>
      </template>
      <template v-slot:item.reviewRating="props">
        <v-chip
          v-if="props.item.reviewRating"
          :color="getReviewColor(props.item.reviewRating)"
          small
          dark
        >
          {{ formatReviewRating(props.item.reviewRating) }}
        </v-chip>
        <span v-else>-</span>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import gql from "graphql-tag";
import { mapGetters } from "vuex";
import filter from "lodash/filter";
import groupBy from "lodash/groupBy";
import sortBy from "lodash/sortBy";
import debounce from "lodash/debounce";
// import get from "lodash/get";
import OrgClosedCasesInPeriod from "../charts/OrgClosedCasesInPeriod";
import OrgToDByAttorney from "../charts/OrgToDByAttorney";
import VueJsonToCsv from "vue-json-to-csv";

export default {
  data() {
    return {
      search: "",
      headers: [
        { text: "Action", value: "caseId", width: "100px" },
        { text: "Group", value: "caseGroup" },
        { text: "State", value: "state" },
        { text: "Client", value: "clientName" },
        { text: "Attorney", value: "attorney" },
        { text: "Paralegal", value: "paralegal" },
        { text: "Case Rank", value: "caseRank" },
        { text: "Review", value: "reviewRating" },
        { text: "Review Date", value: "reviewDate", visible: false },
        { text: "ToD", value: "tod" },
        { text: "Total Fees", value: "totalFees" },
        { text: "Litigation Status", value: "litigationStatus" },
        { text: "Opened", value: "dateOpened", visible: false },
        { text: "Wanted", value: "wantedDate", visible: false },
        { text: "Retained", value: "retainedDate", visible: false },
        { text: "Demand", value: "demandDate", visible: false },
        { text: "Closed", value: "dateClosed" },
        { text: "Reason", value: "reasonClosed" }
      ],
      closedCases: [],
      retainedOnly: true,
      showByAttorney: false,
      showCharts: false,
      needsSettlementOnly: false,
      reviewFilter: "all",
      reviewFilterOptions: [
        { text: "All Cases", value: "all" },
        { text: "Has Review", value: "hasReview" },
        { text: "No Review", value: "noReview" },
        { text: "5-Star", value: "5-star" },
        { text: "4-Star", value: "4-star" },
        { text: "3-Star", value: "3-star" },
        { text: "2-Star", value: "2-star" },
        { text: "1-Star", value: "1-star" }
      ],
      filteredResults: [],
      totalFilteredResults: 0,
      firedReasons: [
        "CSA FIRED BY CLIENT",
        "CSA FIRED BY CLIENT - CLIENT SETTLED CASE",
        "CSA FIRED BY CLIENT - RETAINED ANOTHER ATTORNEY",
        "REJECT - RETAINED ANOTHER ATTORNEY",
        "INTAKE - RETAINED ANOTHER ATTORNEY"
      ],
      excludedAttorneys: [
        "null",
        "Townsend Jeff",
        "Dyer Erin",
        "Black Laura",
        "Anderson Mike",
        "Swapp Ryan",
        "Berrett Cameron",
        "Feit Brad",
        "Fawson Josh",
        "Moors Lindsey",
        "Anderson Brad"
      ],
      visibleColumns: {
        dateOpened: false,
        wantedDate: false,
        retainedDate: false,
        demandDate: false,
        reviewDate: false
      }
    };
  },
  components: {
    OrgClosedCasesInPeriod,
    VueJsonToCsv,
    OrgToDByAttorney
  },
  methods: {
    updateSearch: debounce(function(e) {
      this.search = e.target.value;
    }, 500),
    shortenLabel(label) {
      if (/REJECT -/.test(label)) {
        return label.substr(9, 100);
      }
      if (/CASE SETTLED/.test(label)) {
        return "SETTLED";
      }
      return label;
    },
    getFiltered(items) {
      if (this.totalFilteredResults !== items?.length) {
        this.totalFilteredResults = items?.length;
      }
      if (JSON.stringify(this.filteredResults) !== JSON.stringify(items)) {
        this.filteredResults = items ? [...items] : [];
      }
      return false;
    },
    intakeRank(rank) {
      if (!rank) return null;
      let num = rank.substr(0, 1);
      return parseInt(num, 10);
    },
    toggleColumnVisibility(columnValue) {
      this.visibleColumns[columnValue] = !this.visibleColumns[columnValue];
    },
    isColumnVisible(columnValue) {
      return this.visibleColumns[columnValue];
    },
    getRowClass(item) {
      // Highlight rows that need a fully settled date
      // Check if feesWithoutSettlement is truthy (could be true, non-zero number, etc.)
      if (item.feesWithoutSettlement) {
        return "needs-settlement";
      }
      return "";
    },
    formatReviewRating(rating) {
      if (!rating) return "";
      // Extract just the star portion (e.g., "5-Star" from "5-Star Review")
      const match = rating.match(/(\d)-star/i);
      return match ? `${match[1]}★` : rating;
    },
    getReviewColor(rating) {
      if (!rating) return "grey";
      const lowerRating = rating.toLowerCase();
      if (lowerRating.includes("5-star")) return "green";
      if (lowerRating.includes("4-star")) return "light-green";
      if (lowerRating.includes("3-star")) return "orange";
      if (lowerRating.includes("2-star")) return "deep-orange";
      if (lowerRating.includes("1-star")) return "red";
      return "grey";
    }
  },
  computed: {
    ...mapGetters(["startDate", "endDate"]),
    optionalHeaders() {
      return this.headers.filter(header => header.visible === false);
    },
    visibleHeaders() {
      return this.headers.filter(header => {
        if (header.visible === false) {
          return this.visibleColumns[header.value];
        }
        return true;
      });
    },
    averageTod() {
      if (this.filteredResults?.length === 0) return 0;
      let casesSettled = this.filteredResults?.filter(
        c =>
          c.reasonClosed === "CASE SETTLED" &&
          !this.excludedAttorneys.includes(c.attorney)
      );
      let totalTod = casesSettled?.reduce((acc, c) => {
        return acc + c.tod;
      }, 0);
      return (totalTod / casesSettled?.length).toFixed(1);
    },
    averageTodByAttorney() {
      if (this.filteredResults?.length === 0) return [];
      let casesSettled = this.filteredResults?.filter(
        c => c.reasonClosed === "CASE SETTLED"
      );
      const attorneyStats = {};

      casesSettled.forEach(c => {
        const { attorney, tod } = c;
        if (!attorneyStats[attorney]) {
          attorneyStats[attorney] = { totalTod: 0, count: 0 };
        }
        attorneyStats[attorney].totalTod += tod;
        attorneyStats[attorney].count++;
      });

      const result = Object.keys(attorneyStats).map(attorney => {
        const { totalTod, count } = attorneyStats[attorney];
        const averageTod = Number((totalTod / count).toFixed(1));
        return { attorney, averageTod };
      });

      return sortBy(result, [s => s.averageTod]).filter(
        a => !this.excludedAttorneys.includes(a.attorney)
      );
    },
    notSettledAfterDemand() {
      // return the number of cases that do not have reasonClosed === "CASE SETTLED" and c.demandDate is not null
      return filter(this.casesFiltered, c => {
        return c.reasonClosed !== "CASE SETTLED" && !!c.demandDate;
      }).length;
    },
    closedWithoutFees() {
      return filter(this.casesFiltered, c => {
        return !c.totalFees;
      }).length;
    },
    closedWithoutFeesPercentage() {
      if (!this.totalFilteredResults) return "0.0";
      return (
        (this.closedWithoutFees / this.totalFilteredResults) *
        100
      ).toFixed(1);
    },
    firedByClient() {
      return filter(this.casesFiltered, c => {
        return this.firedReasons.includes(c.reasonClosed);
      }).length;
    },
    firedByClientPercentage() {
      if (!this.totalFilteredResults) return "0.0";
      return ((this.firedByClient / this.totalFilteredResults) * 100).toFixed(
        1
      );
    },
    notSettledAfterDemandPercentage() {
      return (
        (this.notSettledAfterDemand / this.totalFilteredResults) *
        100
      ).toFixed(1);
    },
    orgClosedCasesOptions() {
      let groupedByReason = groupBy(this.casesFiltered, c => {
        return c.reasonClosed;
      });
      let series = Object.keys(groupedByReason).map(r => {
        return { name: this.shortenLabel(r), y: groupedByReason[r].length };
      });
      series = sortBy(series, [s => s.name]);
      let parent = this;
      return {
        plotOptions: {
          column: {
            dataLabels: {
              enabled: true,
              formatter: function() {
                return `${this.y} (${(
                  (this.y / parent.casesFiltered.length) *
                  100
                ).toFixed(2)}%)`;
              }
            }
          }
        },
        xAxis: {
          type: "category"
        },
        series: [
          {
            name: "Closed in Period",
            data: series
          }
        ]
      };
    },
    statsByCaseGroup() {
      const grouped = groupBy(this.casesFiltered, c => {
        const group = c.caseGroup || "Unknown";
        if (c.referredOut) return `${group}|||Referred Out`;
        return `${group}|||${c.litigationStatus || "Pre-Litigation"}`;
      });
      const rows = Object.keys(grouped).map(key => {
        const [caseGroup, litigationStatus] = key.split("|||");
        const cases = grouped[key];
        const total = cases.length;
        const notSettledAfterDemand = cases.filter(
          c => c.reasonClosed !== "CASE SETTLED" && !!c.demandDate
        ).length;
        const settledWithFees = cases.filter(
          c =>
            c.reasonClosed === "CASE SETTLED" &&
            !this.excludedAttorneys.includes(c.attorney) &&
            !!c.totalFees
        );
        const avgTod =
          settledWithFees.length > 0
            ? (
                settledWithFees.reduce((acc, c) => acc + c.tod, 0) /
                settledWithFees.length
              ).toFixed(1)
            : "-";
        const noFeesCases = cases.filter(c => !c.totalFees);
        const avgTodNoFees =
          noFeesCases.length > 0
            ? (
                noFeesCases.reduce((acc, c) => acc + c.tod, 0) /
                noFeesCases.length
              ).toFixed(1)
            : "-";
        const noFees = noFeesCases.length;
        const hasFees = cases.filter(c => !!c.totalFees).length;
        const fired = cases.filter(c =>
          this.firedReasons.includes(c.reasonClosed)
        ).length;
        return {
          key,
          caseGroup,
          litigationStatus,
          total,
          notSettledAfterDemand,
          notSettledAfterDemandPct:
            total > 0
              ? ((notSettledAfterDemand / total) * 100).toFixed(1)
              : "0.0",
          avgTod,
          avgTodNoFees,
          hasFees,
          noFees,
          noFeesPct: total > 0 ? ((noFees / total) * 100).toFixed(1) : "0.0",
          fired,
          firedPct: total > 0 ? ((fired / total) * 100).toFixed(1) : "0.0"
        };
      });
      return sortBy(rows, [r => r.caseGroup, r => r.litigationStatus]);
    },
    statsByCaseGroupTotals() {
      const rows = this.statsByCaseGroup;
      const total = rows.reduce((acc, r) => acc + r.total, 0);
      const notSettledAfterDemand = rows.reduce(
        (acc, r) => acc + r.notSettledAfterDemand,
        0
      );
      const hasFees = rows.reduce((acc, r) => acc + r.hasFees, 0);
      const noFees = rows.reduce((acc, r) => acc + r.noFees, 0);
      const fired = rows.reduce((acc, r) => acc + r.fired, 0);
      const settledWithFees = this.casesFiltered.filter(
        c =>
          c.reasonClosed === "CASE SETTLED" &&
          !this.excludedAttorneys.includes(c.attorney) &&
          !!c.totalFees
      );
      const avgTod =
        settledWithFees.length > 0
          ? (
              settledWithFees.reduce((acc, c) => acc + c.tod, 0) /
              settledWithFees.length
            ).toFixed(1)
          : "-";
      const allNoFees = this.casesFiltered.filter(c => !c.totalFees);
      const avgTodNoFees =
        allNoFees.length > 0
          ? (
              allNoFees.reduce((acc, c) => acc + c.tod, 0) / allNoFees.length
            ).toFixed(1)
          : "-";
      return {
        total,
        notSettledAfterDemand,
        notSettledAfterDemandPct:
          total > 0
            ? ((notSettledAfterDemand / total) * 100).toFixed(1)
            : "0.0",
        avgTod,
        avgTodNoFees,
        hasFees,
        noFees,
        noFeesPct: total > 0 ? ((noFees / total) * 100).toFixed(1) : "0.0",
        fired,
        firedPct: total > 0 ? ((fired / total) * 100).toFixed(1) : "0.0"
      };
    },
    closedByAttorneyOptions() {
      let groupedByAttorney = groupBy(this.casesFiltered, c => {
        return c.attorney;
      });
      let attorneys = Object.keys(groupedByAttorney);

      let series = sortBy(
        attorneys.map(c => {
          return {
            name: c,
            y: groupedByAttorney[c].length
          };
        }),
        [s => s.name]
      );

      return {
        xAxis: {
          type: "category"
        },
        series: [
          {
            name: "By Attorney",
            data: series
          }
        ]
      };
    },
    casesFiltered() {
      let filtered = this.closedCases;

      // Apply retained only filter
      if (this.retainedOnly) {
        filtered = filter(filtered, function(c) {
          return !!c.retainedDate;
        });
      }

      // Apply needs settlement filter
      if (this.needsSettlementOnly) {
        filtered = filter(filtered, function(c) {
          return !!c.feesWithoutSettlement;
        });
      }

      // Apply review filter
      if (this.reviewFilter !== "all") {
        if (this.reviewFilter === "hasReview") {
          filtered = filter(filtered, function(c) {
            return !!c.reviewRating;
          });
        } else if (this.reviewFilter === "noReview") {
          filtered = filter(filtered, function(c) {
            return !c.reviewRating;
          });
        } else {
          // Filter by specific star rating (e.g., "5-star", "4-star", etc.)
          const starFilter = this.reviewFilter;
          filtered = filter(filtered, function(c) {
            return (
              c.reviewRating &&
              c.reviewRating.toLowerCase().includes(starFilter)
            );
          });
        }
      }

      return filtered;
    }
  },
  apollo: {
    closedCases: {
      query: gql`
        query($startDate: String!, $endDate: String!) {
          closedCases: closedCasesInPeriod(
            startDate: $startDate
            endDate: $endDate
          ) {
            caseId
            type
            state
            clientName
            paralegal
            attorney
            dateOpened
            dateClosed
            retainedDate
            wantedDate
            reasonClosed
            intakeRank
            caseRank
            reviewRating
            reviewDate
            demandDate
            tod
            totalFees
            litigationStatus
            feesWithoutSettlement
            caseGroup
            referredOut
          }
        }
      `,
      variables() {
        return {
          startDate: this.startDate,
          endDate: this.endDate
        };
      }
    }
  }
};
</script>

<style>
/* Row highlighting for cases that need settlement date */
tr.needs-settlement {
  background-color: #ffebee !important;
}

tr.needs-settlement:hover {
  background-color: #ffcdd2 !important;
}

tr.needs-settlement td {
  background-color: inherit !important;
}
</style>
